<template>
  <div class="time-picker">
    <el-input
      ref="input"
      v-model="text"
      :placeholder="placeholder"
      :disabled="disabled"
      :prefix-icon="Clock"
      inputmode="numeric"
      autocomplete="off"
      @focus="onFocus"
      @click="onClick"
      @blur="onBlur"
      @input="onInput"
      @keydown="onKeydown"
    />
    <!-- Teleported so the list is never clipped by a scrolling modal -->
    <teleport to="body">
      <ul v-if="open" ref="list" class="time-picker__list" :style="listStyle" @mousedown.prevent>
        <li
          v-for="(slot, i) in slots"
          :key="slot"
          :class="{ 'is-active': i === activeIndex, 'is-selected': slot === current }"
          @click="pick(slot)"
        >
          {{ slot }}
        </li>
      </ul>
    </teleport>
  </div>
</template>

<script>
import { shallowRef } from 'vue';
import { Clock } from '@element-plus/icons-vue';

const STEP_MINUTES = 15;
const LIST_MAX_HEIGHT = 252;
const pad = n => String(n).padStart(2, '0');
const BASE_SLOTS = Array.from({ length: (24 * 60) / STEP_MINUTES }, (_, i) => {
  const minutes = i * STEP_MINUTES;
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
});

// Lenient 24h parser: "8" → 08:00, "830" → 08:30, "1745" → 17:45, "8:30" / "8.30" / "8,30" → 08:30.
// Returns '' for empty input and null when the input isn't a valid time.
export function parseTime(input) {
  const s = (input || '').trim().replace(/[:.,\s]+$/, '');
  if (!s) return '';
  let h;
  let m;
  const parts = s.split(/[:.,\s]+/);
  if (parts.length === 2 && /^\d{1,2}$/.test(parts[0]) && /^\d{1,2}$/.test(parts[1])) {
    h = Number(parts[0]);
    m = Number(parts[1]);
  } else if (/^\d{1,4}$/.test(s)) {
    h = Number(s.length <= 2 ? s : s.slice(0, -2));
    m = s.length <= 2 ? 0 : Number(s.slice(-2));
  } else {
    return null;
  }
  if (h > 23 || m > 59) return null;
  return `${pad(h)}:${pad(m)}`;
}

export default {
  name: 'TimePicker',
  props: {
    // "HH:mm" (24h) or empty
    modelValue: { type: String, default: '' },
    placeholder: { type: String, default: '--:--' },
    disabled: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      Clock: shallowRef(Clock),
      // Last committed value; compared against instead of the prop, which lags until the parent re-renders
      current: this.modelValue || '',
      text: this.modelValue || '',
      open: false,
      activeIndex: -1,
      listStyle: {},
      selectOnClick: false,
    };
  },
  computed: {
    slots() {
      const v = this.current;
      // Keep an off-step value (e.g. 08:07) visible and selected in the list
      if (!v || BASE_SLOTS.includes(v)) return BASE_SLOTS;
      return [...BASE_SLOTS, v].sort();
    },
  },
  watch: {
    modelValue(v) {
      this.current = v || '';
      this.text = this.current;
    },
  },
  beforeUnmount() {
    this.closeList();
  },
  methods: {
    onFocus() {
      this.$refs.input.select();
      // A mouse click's mouseup would undo the selection, so re-select on that click
      this.selectOnClick = true;
      this.openList();
    },
    onClick() {
      // Re-select when reopening an already focused field (e.g. after Enter) so typing replaces the time
      if (this.selectOnClick || !this.open) {
        this.$refs.input.select();
        this.selectOnClick = false;
      }
      this.openList();
    },
    onBlur() {
      this.selectOnClick = false;
      this.commit();
      this.closeList();
    },
    onInput() {
      this.openList();
      const parsed = parseTime(this.text);
      if (parsed) {
        this.activeIndex = this.nearestIndex(parsed);
        this.scrollToIndex(this.activeIndex, true);
      }
    },
    onKeydown(e) {
      this.selectOnClick = false;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!this.open) {
          this.openList();
          return;
        }
        if (this.activeIndex < 0) {
          this.activeIndex = this.nearestIndex(parseTime(this.text) || nowHHmm());
        } else {
          const next = this.activeIndex + (e.key === 'ArrowDown' ? 1 : -1);
          this.activeIndex = Math.min(Math.max(next, 0), this.slots.length - 1);
        }
        this.text = this.slots[this.activeIndex];
        this.scrollToIndex(this.activeIndex, false);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        this.commit();
        this.closeList();
      } else if (e.key === 'Escape' && this.open) {
        // Close only the list, not the surrounding modal
        e.stopPropagation();
        this.text = this.current;
        this.closeList();
      }
    },
    pick(slot) {
      this.text = slot;
      this.commit();
      this.closeList();
      // Drop focus so the on-screen keyboard closes on phones
      this.$refs.input.blur();
    },
    commit() {
      const value = parseTime(this.text);
      if (value === null) {
        this.text = this.current;
        return;
      }
      this.text = value;
      if (value !== this.current) {
        this.current = value;
        this.$emit('update:modelValue', value);
        this.$emit('change', value);
      }
    },
    openList() {
      if (this.open || this.disabled) return;
      this.open = true;
      this.activeIndex = this.slots.indexOf(this.current);
      this.updatePosition();
      window.addEventListener('scroll', this.onViewportChange, true);
      window.addEventListener('resize', this.onViewportChange);
      if (window.visualViewport) window.visualViewport.addEventListener('resize', this.onViewportChange);
      const target = this.activeIndex >= 0 ? this.activeIndex : this.nearestIndex(nowHHmm());
      this.$nextTick(() => this.scrollToIndex(target, true));
    },
    closeList() {
      if (!this.open) return;
      this.open = false;
      window.removeEventListener('scroll', this.onViewportChange, true);
      window.removeEventListener('resize', this.onViewportChange);
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.onViewportChange);
    },
    onViewportChange(e) {
      if (e && e.target === this.$refs.list) return;
      this.updatePosition();
    },
    updatePosition() {
      const rect = this.$el.getBoundingClientRect();
      // visualViewport excludes the on-screen keyboard on phones
      const vv = window.visualViewport;
      const viewBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
      const spaceBelow = viewBottom - rect.bottom;
      const style = { left: `${rect.left}px`, width: `${Math.max(rect.width, 110)}px` };
      if (spaceBelow < LIST_MAX_HEIGHT + 8 && rect.top > spaceBelow) {
        style.bottom = `${window.innerHeight - rect.top + 4}px`;
      } else {
        style.top = `${rect.bottom + 4}px`;
      }
      this.listStyle = style;
    },
    nearestIndex(hhmm) {
      // Last slot at or before the given time ("HH:mm" strings compare correctly as text)
      let index = 0;
      this.slots.forEach((slot, i) => {
        if (slot <= hhmm) index = i;
      });
      return index;
    },
    scrollToIndex(index, center) {
      const list = this.$refs.list;
      const item = list && list.children[index];
      if (!item) return;
      if (center) {
        list.scrollTop = item.offsetTop - (list.clientHeight - item.offsetHeight) / 2;
      } else {
        item.scrollIntoView({ block: 'nearest' });
      }
    },
  },
};

function nowHHmm() {
  const d = new Date();
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
</script>

<style scoped>
.time-picker__list {
  position: fixed;
  z-index: 3000;
  max-height: 252px;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--el-bg-color-overlay, #fff);
  border: 1px solid var(--el-border-color-light, #e4e7ed);
  border-radius: 4px;
  box-shadow: var(--el-box-shadow-light);
}
.time-picker__list li {
  height: 36px;
  padding: 0 14px;
  line-height: 36px;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  color: var(--el-text-color-regular, #606266);
  cursor: pointer;
  user-select: none;
}
.time-picker__list li.is-active {
  background: var(--el-fill-color-light, #f5f7fa);
}
@media (hover: hover) {
  .time-picker__list li:hover {
    background: var(--el-fill-color-light, #f5f7fa);
  }
}
.time-picker__list li.is-selected {
  color: var(--el-color-primary, #409eff);
  font-weight: 600;
}

/* Dark mode */
body.dark-version .time-picker__list {
  background: #202940;
  border-color: rgba(255, 255, 255, 0.4);
}
body.dark-version .time-picker__list li {
  color: rgba(255, 255, 255, 0.8);
}
body.dark-version .time-picker__list li.is-active,
body.dark-version .time-picker__list li:hover {
  background: #283352;
}
body.dark-version .time-picker__list li.is-selected {
  color: #fff;
}
</style>
